import { Box, Stack, Typography } from '@mui/material';
import { Door, VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { getVisitorGroupsWithAccessToDevice } from 'utils/Lock/Lock';
import { getVisitorGroupsThatHaveLockAssignedDirectly } from 'utils/VisitorGroup/VisitorGroup';
import DrawerTemplate from 'v2/components/Drawer/DrawerTemplate';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import GroupCheckboxList from '../../components/GroupCheckboxList';

export interface EditUserGroupFormValues {
  selectedUserGroupIds: string[];
}

export const FORM_ID = 'edit-user-group-drawer-form';

type AssignDoorToDoorGroupOrVisitorGroup = (
  door: Door,
  doorGroupIds: string[],
  visitorGroupIds: string[]
) => Promise<{ success: boolean; errorMessage?: string }>;

interface Props {
  device: Door;
  visitorGroups: VisitorGroup[];
  assignDoorToDoorGroupOrVisitorGroup: AssignDoorToDoorGroupOrVisitorGroup;
}

// Only rendered once device and visitorGroups have loaded, so the form is initialized with the
// real, current selection as its defaultValues - a later background refetch of this data won't
// touch an already-mounted form's state, so unsaved changes are never silently reset.
const EditUserGroupsForm = ({ device, visitorGroups, assignDoorToDoorGroupOrVisitorGroup }: Props) => {
  const { translation } = useTranslation();
  const { closeDrawer } = useDrawerContext();
  const { showSnackbar } = useSnackbarContext();
  const [error, setError] = useState<string>();

  const viaDeviceGroupsById = new Map(
    getVisitorGroupsWithAccessToDevice(
      device.id,
      device.locations.map((location) => ({ id: location.id, name: location.name })),
      visitorGroups
    ).map((visitorGroup) => [visitorGroup.id, visitorGroup.viaDeviceGroups])
  );

  const {
    handleSubmit,
    control,
    formState: { isSubmitting }
  } = useForm<EditUserGroupFormValues>({
    mode: 'onTouched',
    defaultValues: {
      selectedUserGroupIds: getVisitorGroupsThatHaveLockAssignedDirectly(visitorGroups, device.id).map(
        (visitorGroup) => visitorGroup.id
      )
    }
  });

  const onSubmit = async (data: EditUserGroupFormValues) => {
    setError(undefined);
    // assignDoorToDoorGroupOrVisitorGroup always sets both device groups and user groups in one
    // call. This drawer only edits user groups, so we re-send the device's current groups unchanged.
    const currentDeviceGroupIds = device.locations.map((location) => location.id);
    const result = await assignDoorToDoorGroupOrVisitorGroup(device, currentDeviceGroupIds, data.selectedUserGroupIds);
    if (result.success) {
      showSnackbar(translation.saved_changes, { severity: 'success' });
      closeDrawer();
    } else {
      setError(result.errorMessage ?? translation.error_while_updating_device);
    }
  };

  return (
    <DrawerTemplate
      id="edit-user-group-drawer"
      loading={isSubmitting}
      title={translation.edit_user_groups}
      primaryAction={{
        label: translation.save,
        onClick: handleSubmit(onSubmit),
        loading: isSubmitting,
        form: FORM_ID,
        id: 'save-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'cancel-button' }}
      error={error}
      onDismissError={() => setError(undefined)}
    >
      <Stack
        id={FORM_ID}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        gap={3}
        sx={{
          height: '100%',
          minHeight: 0
        }}
      >
        <Typography variant="body2" color="textSecondary">
          {translation.select_user_group_membership.replace('{{name}}', device.name)}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <GroupCheckboxList
            items={visitorGroups.map((visitorGroup) => ({
              id: visitorGroup.id,
              name: visitorGroup.name,
              viaDeviceGroups: viaDeviceGroupsById.get(visitorGroup.id) ?? []
            }))}
            name="selectedUserGroupIds"
            control={control}
            searchPlaceholder={translation.search_user_group}
          />
        </Box>
      </Stack>
    </DrawerTemplate>
  );
};

export default EditUserGroupsForm;
